import { useEffect, useState, useRef, memo } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import ContentList from "../ContentList/component";
import FeaturedArea from "../FeaturedArea/component";
import { SectionHeading } from "../Heading/component";
import DefaultPolicy from "./library/DefaultPolicy";
import DebugPolicy from "./library/DebugPolicy";
import HighDensityPolicy from "./library/HighDensityPolicy";
import MediumDensityPolicy from "./library/MediumDensityPolicy";
import LowDensityPolicy from "./library/LowDensityPolicy";
import FeaturedPolicy from "./library/FeaturedPolicy";
import ListingPolicy from "./library/ListingPolicy";
import HomepagePolicy from "./library/HomepagePolicy";
import SearchPolicy from "./library/SearchPolicy";
import PageActions from "../../utils/page/library";
import {
    split_content_lists,
    getNumTopStories,
} from "../../utils/dataTransform/utils";
import classNames from "classnames";
import { DISPLAY_POLICIES, FEATURED_DENSE_COUNT } from "./constants";
import { SECTIONS_WITH_SLIDER } from "../../constants/sections";
import Slider from "../Slider/component";
import { NATIVE_AD_TYPE } from "../Ad/constants";
import Card from "../Card/component";
import { CARD_TYPES } from "../Card/constants";
import { Sidebar } from "./components/Sidebar/component";

/**
 * This component renders the areas that are shown on every landing
 * page, e.g. FeaturedArea and ContentList
 */
const ContentArea = ({
    content = null,
    density = DISPLAY_POLICIES.default,
    hasFeature = true,
    hasSidebar = true,
    hasTopStories = false,
    hasHighlightedStories = true,
    listHeading = null,
    className,
    hasNativeAd = false,
    nativeAdCallback = null,
    nativeAdFailed = null,
    cardClickHandler = () => {},
    isDenseFeatured = false,
    cutoffIndex = FEATURED_DENSE_COUNT,
    beforeContent = null,
    contentPackage = null,
    loadMoreFetch = () => {},
    loadMore,
    hiddenA11yHeader,
    viewport,
    featureAttributionData,
    sidebarWidgets,
    id,
}) => {
    const [wallpaper, setWallpaper] = useState(false);
    const domNode = useRef(null);

    const baseSection = useSelector((state) => state.app.baseSection);
    const subSection = useSelector((state) => state.app.subSection);
    const path = useSelector((state) => state.app.path);

    useEffect(() => {
        // In case there's a wallpaper Ad, we'll need to adjust the classnames and layout
        const componentInfo = {
            domNode: domNode,
            callback: wallpaperCallback,
        };
        PageActions.registerWallpaper(componentInfo);
    }, []);

    /**
     * Fires as a callback in case the wallpaper Ad is enabled
     * @param {bool} isEnabled Indicates whether the wallpaper is enabled or not
     * @returns {void}
     */
    const wallpaperCallback = (isEnabled) => {
        if (wallpaper !== isEnabled) {
            setWallpaper(isEnabled);
        }
    };

    /**
     * Returns the JSX markup for a content list based on the section
     * @param {object} contentList The content list to be rendered
     * @param {object} displayPolicy Can be one of: Debug, Default, High Density, Medium Density etc.
     * @returns {object} The JSX markup to be rendered
     */
    const getContentList = (contentList, displayPolicy) => {
        // Determine the section to display based on the matched section.
        if (contentList && contentList.length) {
            const listProps = {
                baseSection: baseSection,
                subSection: subSection,
                items: contentList,
                displayPolicy: displayPolicy,
                loadMore: loadMore,
                trackingSection: "contentlist",
                cardClickHandler: cardClickHandler,
                loadMoreFetch: loadMoreFetch,
            };

            const contentArealistHeading = listHeading ? (
                hiddenA11yHeader ? (
                    <h2 className="listHeading a11y">{listHeading}</h2>
                ) : (
                    <SectionHeading text={listHeading} />
                )
            ) : null;

            return (
                <div className="contentListWrapper">
                    {contentArealistHeading}
                    <ContentList {...listProps} />
                </div>
            );
        }
    };

    /**
     * Returns the JSX markup for a featured list based on the section
     * In certain situations, content package can appear within the FeaturedArea,
     * thus changing the policy to not render the TopStories layout
     * @param {object} featuredList The featured list to be rendered
     * @param {object} displayPolicy Can be one of: Debug, Default, High Density, Medium Density etc.
     * @returns {object} The JSX markup to be rendered
     */
    const getFeaturedList = (
        featuredList,
        displayPolicy,
        packageInFeaturedArea
    ) => {
        if (hasFeature && featuredList) {
            // Determine the section to display based on the matched section.
            const listProps = {
                baseSection: baseSection,
                subSection: subSection,
                items: featuredList,
                displayPolicy: displayPolicy,
                path,
                viewport: viewport,
                hasTopStories: contentPackage ? false : hasTopStories,
                hasHighlightedStories: hasHighlightedStories,
                cardClickHandler: cardClickHandler,
                isDenseFeatured: isDenseFeatured,
                cutoffIndex: cutoffIndex,
                contentPackage: packageInFeaturedArea,
            };
            return <FeaturedArea {...listProps} />;
        }
    };
    /**
     * Returns the JSX markup for a trending list based on the section
     * @returns {object} The display and feature policy
     */
    const getDisplayPolicy = () => {
        const featuredProps = {
            viewport: viewport,
            hasTopStories: contentPackage ? false : hasTopStories,
            contentPackage: contentPackage,
        };

        let displayPolicy = null;
        let featurePolicy = null;

        switch (density) {
            case DISPLAY_POLICIES.debug:
                displayPolicy = new DebugPolicy();
                featurePolicy = new FeaturedPolicy(featuredProps);
                break;
            case DISPLAY_POLICIES.high:
                displayPolicy = new HighDensityPolicy();
                featurePolicy = displayPolicy;
                break;
            case DISPLAY_POLICIES.medium:
                displayPolicy = new MediumDensityPolicy();
                featurePolicy = displayPolicy;
                break;
            case DISPLAY_POLICIES.listing:
                displayPolicy = new ListingPolicy();
                featurePolicy = displayPolicy;
                break;
            case DISPLAY_POLICIES.homepage:
                displayPolicy = new DefaultPolicy();
                featurePolicy = new HomepagePolicy(featuredProps);
                break;
            case DISPLAY_POLICIES.rich:
                displayPolicy = new LowDensityPolicy();
                featuredProps.richPolicy = true;
                featurePolicy = new FeaturedPolicy(featuredProps);
                break;
            case DISPLAY_POLICIES.search:
                displayPolicy = new SearchPolicy();
                featurePolicy = displayPolicy;
                break;
            default:
                displayPolicy = new DefaultPolicy();
                featurePolicy = new FeaturedPolicy(featuredProps);
                break;
        }

        return [displayPolicy, featurePolicy];
    };

    /**
     * This function creates a Card for the content package item
     * @return {component} The CardContentPackage component via Card
     */
    const getContentPackageCard = () => {
        if (!contentPackage) {
            return null;
        }

        return (
            <Card
                type={CARD_TYPES.TYPE_CONTENT_PACKAGE}
                {...contentPackage}
                key={contentPackage.contentId}
            />
        );
    };

    const wrapperClasses = classNames(
        "pageContentWrap",
        viewport,
        { wallpaperArea: wallpaper },
        className
    );
    const contentClasses = classNames(
        "contentAreaWrap",
        className,
        hasSidebar ? "withSidebar" : ""
    );

    let items = Array.isArray(content) ? [...content] : null;
    let contentList = null;
    let featuredList = null;
    if (featureAttributionData) {
        items.feature = featureAttributionData;
    }

    const contentPackageCard = getContentPackageCard();
    const packageInFeaturedArea =
        hasFeature && contentPackageCard ? contentPackageCard : null;

    // If the first item is of the type contentpackage, remove it from the list so it doesn't appear twice on the page
    // It will be rendered via the contentPackageCard
    if (contentPackageCard) {
        const [, ...rest] = items;
        items = rest;
    }

    // If there's no Featured area, nor a Content package e.g. search page
    if (!hasFeature) {
        contentList = items;
    } else {
        // Split the Featured area cards from the rest
        // If there's a ContentPackage on the page, don't show "Top Stories" layout
        const numTopStories =
            hasTopStories && !contentPackage
                ? getNumTopStories(baseSection)
                : 0;
        const contentAreaCutoffIndex = isDenseFeatured ? cutoffIndex : 0;
        if (hasNativeAd && content.length !== 0 && !nativeAdFailed) {
            let adIndex = contentPackage ? 4 : 5;
            items.splice(adIndex, 0, {
                key: `key-native-ad-${adIndex}`,
                type: NATIVE_AD_TYPE,
                targeting: { plc: "feed" },
                callback: nativeAdCallback,
            });
        }

        [featuredList, contentList] = split_content_lists(
            items,
            numTopStories,
            hasHighlightedStories,
            contentAreaCutoffIndex
        );
    }

    const [displayPolicy, featurePolicy] = getDisplayPolicy();

    let slider = null;
    const sliderConfig = SECTIONS_WITH_SLIDER[path];
    if (sliderConfig) {
        slider = (
            <Slider
                header={sliderConfig.header}
                viewport={viewport}
                section={sliderConfig.section}
                itemCount={sliderConfig.itemCount}
            />
        );
    }
    return (
        <div
            className={wrapperClasses}
            ref={domNode}
            id={id}
            data-testid="content-area"
        >
            {slider}

            <section className={contentClasses}>
                <div className="withFlex">
                    <div data-testid="contentArea" className="contentArea">
                        {beforeContent}
                        {getFeaturedList(
                            featuredList,
                            featurePolicy,
                            packageInFeaturedArea
                        )}
                        {getContentList(contentList, displayPolicy)}
                    </div>
                </div>
                {hasSidebar && (
                    <div className="withFlex">
                        <Sidebar
                            viewport={viewport}
                            sidebarWidgets={sidebarWidgets}
                        />
                    </div>
                )}
            </section>
        </div>
    );
};

ContentArea.propTypes = {
    content: PropTypes.array,
    density: PropTypes.string,
    hasFeature: PropTypes.bool,
    hasSidebar: PropTypes.bool,
    hasTopStories: PropTypes.bool,
    hasHighlightedStories: PropTypes.bool,
    listHeading: PropTypes.string,
    hiddenA11yHeader: PropTypes.bool,
    className: PropTypes.string,
    sidebarWidgets: PropTypes.array,
    hasNativeAd: PropTypes.bool,
    nativeAdCallback: PropTypes.func,
    nativeAdFailed: PropTypes.bool,
    cardClickHandler: PropTypes.func,
    isDenseFeatured: PropTypes.bool,
    cutoffIndex: PropTypes.number,
    beforeContent: PropTypes.element,
    contentPackage: PropTypes.object,
    loadMoreFetch: PropTypes.func,
    featureAttributionData: PropTypes.object,
};

export default memo(ContentArea);
